import { useQuery } from '@apollo/client';
import { GetComplianceMonitoringAssessmentCountDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useIsModuleEnabled } from 'src/hooks/useIsModuleEnabled';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

/**
 * Gates the optional Oversight rating columns on the Risk Register and Compliance Register.
 *
 * Returns true when either:
 *   - the user's role carries `read:compliance_monitoring_assessment` AND the
 *     Oversight module is enabled for the org, OR
 *   - the user is owner/contributor on at least one oversight assessment
 *     (visible via Hasura row-level perms). In this case the module flag is
 *     intentionally ignored: if the user is already assigned to a CMA, the
 *     data exists for them and they should be able to surface its rating
 *     regardless of the org's module toggle.
 *
 * While the permission query is still loading, the aggregate count query is
 * deferred so role-permission users (who don't need the count) don't fire it
 * unnecessarily. Returns `false` during loading — column config will hydrate
 * once the decision is stable.
 */
export const useCanViewOversightColumns = (): boolean => {
  const oversightModuleEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );

  const { hasPermission: hasRolePermission, loading: permissionLoading } =
    useHasPermissionQuery('read:compliance_monitoring_assessment');

  const roleGrants = hasRolePermission && oversightModuleEnabled;

  const { data } = useQuery(GetComplianceMonitoringAssessmentCountDocument, {
    skip: permissionLoading || roleGrants,
    fetchPolicy: 'cache-first',
  });
  const hasContributions =
    (data?.compliance_monitoring_assessment_aggregate.aggregate?.count ?? 0) >
    0;

  if (permissionLoading) {
    return false;
  }

  return roleGrants || hasContributions;
};
